import View from 'views/base/view';
import DialogTemplate from './templates/dialog';
import IconLoadService from 'services/loader-handler-services/icon-load-service';
// the import below is required, do not be fooled by its lack of use
// eslint-disable-next-line no-unused-vars
import DialogContentView from './dialog-content-view';
import { KeybindingService, keys } from 'services/event-handler-services/keybinding-service';
import utils from 'lib/utils';
import Chaplin from 'chaplin';
import $ from 'jquery';
import FocusService from 'services/event-handler-services/focus-service';

const { Escape } = keys;
const DialogView = View.extend({
  noWrap: true,
  autoRender: true,
  template: DialogTemplate,
  listen: {
    addedToDOM: 'loaded',
  },
  contentView: null,
  regions: {
    atomDialogRegion: `[data-content="dialog-atom-region"]`,
  },
  model: new Chaplin.Model({}),
  hideOnKeydownHandler: null,

  initialize() {
    View.prototype.initialize.apply(this, arguments);
  },

  getTemplateData: function () {
    return this.model.attributes;
  },

  getTemplateFunction() {
    return this.template;
  },

  loaded() {
    this.delegate('click', '[data-dismiss="modal"]', function (e) {
      this.hide();
    });

    this.delegate('click', '[data-dismiss="modal"] *', function (e) {
      e.stopPropagation();
      e.target.id && Chaplin.mediator.publish(`dialog:click:${e.target.id}`, e);
    });

    this.subscribeEvent('dialog:set:content', this.setDialogContent);
  },

  dispose() {
    $('body').removeClass('modal-open');
    View.prototype.dispose.apply(this, arguments);
  },

  setDialogContent(options) {
    // Passing in a non-object or no contentView closes the dialog
    if (!$.isPlainObject(options) || options.contentView == null) {
      this.hide();
      return;
    }

    const { contentView, id } = options;

    // Handle existing dialog content
    if (this.contentView !== null) {
      // No need to do anything if it's already showing
      if (this.contentView === contentView) {
        return;
      }
      // Otherwise, hide and clear current content
      this.hide();
    }
    this.setDialogClassesAndAttributes(options);

    this.contentView = options.contentView;
    this.model.set('id', id);
    contentView.region = 'atomDialogRegion';
    this.subview(this.atomDialogRegion, contentView);

    // Attach the contentView to the dialog region.
    contentView.attach();

    // Attach the subviews of the contentView (e.g. nested blocks).
    contentView.subviews.forEach(sv => sv.attach({ ...sv }));

    // IconLoadService requires the elements to be in the page DOM.
    // We also need to ensure we flexibly load icons scoped to any connected
    // models, but not break if the contentView and/or parent is missing one.
    if (contentView.model && contentView.model.has('cid')) {
      IconLoadService.applyIcon('far fa-xmark-large', contentView.model.get('cid'), 'fa-lg');
      this.applyButtonIcons(contentView.model);
    }

    if (contentView.parent && contentView.parent.model) {
      const parentModel = contentView.parent.model;
      if (parentModel.has('cid')) {
        IconLoadService.applyIcon('far fa-xmark-large', parentModel.get('cid'), 'fa-lg');
        this.applyButtonIcons(parentModel);
      }
      if (parentModel.has('focusOnHideSelector')) {
        this.model.set('focusOnHideSelector', parentModel.get('focusOnHideSelector'));
      }
    }

    const modalBlocksView = this.contentView?.subviewsByName[`${this.contentView?.model.get('cid')}-modal-blocks`];
    //making sure that literally everything in the modal is loaded.
    this.waitForAllBlocksToLoad(modalBlocksView).then(() => {
      Chaplin.mediator.publish('dialog:content:loaded', { id: contentView?.model.get('id') });
      this.trapFocus(`#${contentView?.model.get('id')}_label`);
    });

    contentView.subviews.forEach(sv => {
      sv.trigger('page:state:change', sv.parent);
    });
    this.show();

    /**
     * Trigger esc event on the document so that the learner does not need to be focused on
     * the modal to dismiss it.
     *
     * Note:  for assistive technologies such as voiceover, it instructs the learner to double tap in order to escape (triggering modal close)
     */

    $(document).on(
      'keydown',
      (this.hideOnKeydownHandler = e => {
        KeybindingService.with(e).bind(Escape, () => {
          this.hide();
          e.preventDefault();
          e.stopImmediatePropagation();
        });
      })
    );
  },
  async waitForAllBlocksToLoad(obj) {
    if (obj.nestedLoads && Array.isArray(obj.nestedLoads)) {
      await Promise.all(obj.nestedLoads);
    }
    if (obj.subviews && Array.isArray(obj.subviews)) {
      await Promise.all(obj.subviews.map(async subview => await this.waitForAllBlocksToLoad(subview)));
    }
    return obj;
  },

  setDialogClassesAndAttributes({ modalClass, dialogClass, preventContainerClose }) {
    !!modalClass && this.$el.addClass(modalClass);
    const modalContainer = this.$el.find('.modal__container');
    !!dialogClass && modalContainer.addClass(dialogClass);
    modalContainer.attr('data-dismiss', !preventContainerClose ? 'modal' : null);
  },

  resetDialogClasses() {
    this.$el.attr('class', 'modal');
    this.$el.find('.modal__container').attr('class', 'modal__container');
  },

  show() {
    $('body').addClass('modal-open');
    this.$el.show().addClass('in');
    // trapFocus is called after blocks load via block:state:loaded listener (line ~116)
    this.publishEvent('dialog:show', { id: this.model.get('id') });

    // TODO: Some modals may no have headers, we need to provision for a modal description somewhere

    // Update modal top offset based on a platform header

    const modalContainer = this.$el.find('.modal__container:not(.modal-drawer--bottom)').find('.js-modal__content');

    if (modalContainer.length) {
      const platformHeight = utils.getPlatformHeaderHeight();

      if (platformHeight) {
        modalContainer.css('top', platformHeight);
      }
    }
  },

  trapFocus(focusableElements) {
    if ($('#site-container').hasClass('has-lottie')) {
      Chaplin.mediator.subscribe('lottie:complete', () => {
        FocusService.trapFocus(this, '.js-modal__content', '#site-container', focusableElements);
        this.announceDialogState('expanded');
      });
    } else {
      requestAnimationFrame(() => {
        FocusService.trapFocus(this, '.js-modal__content', '#site-container', focusableElements);
        this.announceDialogState('expanded');
      });
    }
  },

  // Announce dialog state via the live region inside the dialog.
  // Only announces when the modal was triggered by a button with aria-expanded
  // (i.e. a menu/modal trigger button), not for all modal types.
  announceDialogState(message) {
    const triggerHasAriaExpanded = this.model.has('focusOnHideSelector') &&
      $(this.model.get('focusOnHideSelector')).is('[aria-expanded]');
    if (!triggerHasAriaExpanded) return;
    const liveRegion = this.$el.find('#dialog-live-region');
    if (!liveRegion.length) return;
    // Short delay to let the focus and dialog context announcements finish first.
    // Safari VO needs more time as it announces dialog label and item count before processing polite live regions.
    setTimeout(() => {
      liveRegion.text(message);
      setTimeout(() => {
        liveRegion.text('');
      }, 1000);
    }, 500);
  },

  hide() {
    // prevent double trigger of hide with if/else here
    if (this.contentView) {
      this.contentView.$el.detach();
      this.stopListening(
        this.contentView?.subviewsByName[`${this.contentView?.model.get('cid')}-modal-blocks`],
        'page:state:change'
      );
      this.contentView = null;
      $(document).off('keydown', this.hideOnKeydownHandler);
    } else {
      return;
    }
    $('body').removeClass('modal-open');
    this.$el.removeClass('in').hide();
    this.resetDialogClasses();
    if (this.model.has('focusOnHideSelector')) {
      const selector = this.model.get('focusOnHideSelector');
      const $trigger = $(selector);
      // Set aria-expanded to false before focus so VO reads "collapsed" when focus lands
      $trigger.attr('aria-expanded', 'false');
      $trigger.trigger('focus');
    }

    FocusService.releaseFocus();
    this.publishEvent('dialog:hide', { id: this.model.get('id') });
  },

  applyButtonIcons(model) {
    const iconProps = ['button_icon_before', 'button_icon_after'];
    iconProps.forEach(i => {
      if (model.has(i)) {
        const classes = model.get(`${i}_classes`) || null;
        IconLoadService.applyIcon(model.get(i), model.get('cid'), classes);
      }
    });
  },
});

export default DialogView;
